/**
 * HomeEventsSection — Sezione "Eventi in programma" sotto l'hero della homepage.
 *
 * Si auto-nasconde se non ci sono eventi futuri/in corso.
 * Sorgente: GET /api/public/eventi/in-programma
 *
 * Diverso da EventsTeaserSection (che vive DENTRO l'hero): questa \u00e8 standalone.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Calendar, MapPin, ArrowRight, Sparkles } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function fmtDateIT(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("it-IT", { day: "numeric", month: "long" });
  } catch (_) { return iso; }
}

function statusBadge(s) {
  if (s === "in_corso") return { txt: "In corso", cls: "bg-emerald-100 text-emerald-700" };
  if (s === "passato") return { txt: "Passato", cls: "bg-zinc-100 text-zinc-600" };
  return { txt: "In programma", cls: "bg-amber-100 text-amber-700" };
}

export default function HomeEventsSection() {
  const [eventi, setEventi] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/api/public/eventi/in-programma?limit=6`, { withCredentials: true })
      .then((r) => setEventi(r.data?.items || []))
      .catch(() => setEventi([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading || eventi.length === 0) return null;

  return (
    <section
      id="eventi"
      className="py-20 md:py-24 bg-gradient-to-b from-amber-50/30 via-white to-white border-t border-zinc-100"
      data-testid="home-events-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-12">
          <p className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-xs font-bold uppercase tracking-wider mb-4">
            <Sparkles className="w-3.5 h-3.5" /> Eventi Colucci
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-zinc-900 mb-3">
            Vieni a trovarci
          </h2>
          <p className="text-base text-zinc-500 max-w-2xl mx-auto">
            Eventi, fiere e iniziative dove incontrare il team Colucci di persona.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {eventi.map((ev) => {
            const badge = statusBadge(ev.status_label);
            const di = ev.data_inizio;
            const df = ev.data_fine;
            const luogo = ev.luogo || ev.citta || ev.indirizzo_completo || "";
            const locandinaSrc = ev.locandina_url
              ? (ev.locandina_url.startsWith("http")
                  ? `${API}/api/images/ai-rework?src=${encodeURIComponent(ev.locandina_url)}&context=event`
                  : ev.locandina_url)
              : null;
            return (
              <Link
                key={ev.slug}
                to={`/eventi/${ev.slug}`}
                className="group bg-white rounded-2xl border border-zinc-200 overflow-hidden hover:shadow-lg hover:border-emerald-200 transition-all"
                data-testid={`home-event-card-${ev.slug}`}
              >
                <div className="aspect-[4/3] bg-gradient-to-br from-emerald-700 to-emerald-900 relative overflow-hidden">
                  {locandinaSrc ? (
                    <img
                      src={locandinaSrc}
                      alt={ev.titolo}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      onError={(e) => {
                        if (ev.locandina_url && e.currentTarget.src !== ev.locandina_url) {
                          e.currentTarget.src = ev.locandina_url;
                        }
                      }}
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <Calendar className="w-16 h-16 text-white/40" />
                    </div>
                  )}
                  <span className={`absolute top-3 left-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${badge.cls}`}>
                    {badge.txt}
                  </span>
                </div>
                <div className="p-5 space-y-3">
                  <h3 className="font-heading text-lg font-semibold text-zinc-900 line-clamp-2 group-hover:text-emerald-700 transition-colors">
                    {ev.titolo}
                  </h3>
                  {di && (
                    <p className="flex items-center gap-2 text-xs text-zinc-600">
                      <Calendar className="w-3.5 h-3.5 text-emerald-700" />
                      {fmtDateIT(di)}{df && df !== di ? ` — ${fmtDateIT(df)}` : ""}
                    </p>
                  )}
                  {luogo && (
                    <p className="flex items-center gap-2 text-xs text-zinc-600">
                      <MapPin className="w-3.5 h-3.5 text-emerald-700" />
                      <span className="line-clamp-1">{luogo}</span>
                    </p>
                  )}
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 group-hover:gap-2 transition-all">
                    Scopri di più <ArrowRight className="w-3 h-3" />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>

        <div className="text-center mt-10">
          <Link
            to="/eventi"
            className="inline-flex items-center gap-2 px-6 h-12 rounded-full bg-zinc-900 text-white text-sm font-semibold hover:bg-zinc-800 transition-colors"
            data-testid="home-events-cta-all"
          >
            Tutti gli eventi <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
